/**
 * Store Club Portal API Service
 * Handles all API calls for the members-only retail shopping experience.
 */

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

// Helper for API calls with cookie authentication
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include', // Send cookies with requests
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || error.error || 'Request failed');
  }

  return response.json();
};

// Auth API
export const storeClubAuthAPI = {
  register: async (data: {
    name: string;
    email: string;
    phone: string;
    password: string;
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
    animals: Array<{
      animal_type: string;
      name?: string;
      breed?: string;
      count?: number;
      weight_lbs?: number;
      activity_level?: string;
      life_stage?: string;
      notes?: string;
    }>;
    subscribe_to_reminders?: boolean;
    subscribe_to_weather_alerts?: boolean;
  }) => {
    // Map frontend field names to backend field names
    const requestData = {
      ...data,
      weather_alerts_enabled: data.subscribe_to_weather_alerts,
      reminder_days_before: 3,
      preferred_contact: 'email',
    };

    console.log('[StoreClub Register] Sending data:', {
      ...requestData,
      password: requestData.password ? '***' : 'MISSING',
    });

    const response = await fetch(`${API_BASE_URL}/store-club/register/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Registration failed' }));
      console.error('[StoreClub Register] Error response:', errorData);

      // Use the user-friendly detail message from backend
      let errorMessage = errorData.detail;

      // If no detail, try to format errors object
      if (!errorMessage && errorData.errors) {
        const errorParts: string[] = [];
        for (const [field, errors] of Object.entries(errorData.errors)) {
          if (Array.isArray(errors)) {
            errorParts.push(...errors.map((e: any) => String(e)));
          }
        }
        errorMessage = errorParts[0] || 'Registration failed. Please check your information.';
      }

      throw new Error(errorMessage || 'Registration failed');
    }

    const result = await response.json();
    // Transform response to match expected format
    return {
      access: result.tokens?.access,
      refresh: result.tokens?.refresh,
      member: {
        id: result.member_id,
        ...data,
      },
    };
  },

  login: async (email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/store-club/login/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    return response.json();
  },

  logout: async () => {
    try {
      await apiCall('/store-club/auth/logout/', { method: 'POST' });
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('store_club_member');
  },

  me: async () => {
    return apiCall('/store-club/auth/me/');
  },
};

// Member Profile API
export const memberAPI = {
  getProfile: () => apiCall('/store-club/profile/'),

  updateProfile: (data: {
    name?: string;
    phone?: string;
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    subscribe_to_reminders?: boolean;
    subscribe_to_weather_alerts?: boolean;
    preferred_reminder_days_before?: number;
  }) => apiCall('/store-club/profile/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  calculateDistance: () => apiCall('/store-club/calculate-distance/', { method: 'POST' }),
};

// Animals API
export const animalsAPI = {
  getAll: async () => {
    // Auth is via httpOnly cookies with credentials: 'include'
    return apiCall('/store-club/animals/');
  },

  create: (data: {
    category: string;
    animal_type: string;
    name?: string;
    breed?: string;
    count?: number;
    weight_lbs?: number;
    activity_level?: string;
    life_stage?: string;
    notes?: string;
  }) => apiCall('/store-club/animals/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id: number, data: {
    category?: string;
    animal_type?: string;
    name?: string;
    breed?: string;
    count?: number;
    weight_lbs?: number;
    activity_level?: string;
    life_stage?: string;
    notes?: string;
  }) => apiCall(`/store-club/animals/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  delete: (id: number) => apiCall(`/store-club/animals/${id}/`, { method: 'DELETE' }),
};

// Products API
export const productsAPI = {
  getAll: async (category?: string) => {
    let url = '/store-club/products/';
    if (category) url += `?category=${category}`;
    const data = await apiCall(url);
    // Handle both array response and object response with debug info
    if (Array.isArray(data)) {
      return data;
    }
    // If response is object with products array, return that
    if (data && Array.isArray(data.products)) {
      if (data.debug) {
        console.log('[Products API] Debug info:', data.debug);
      }
      return data.products;
    }
    // Fallback to empty array
    console.warn('[Products API] Unexpected response format:', data);
    return [];
  },

  getBYOCStock: () => apiCall('/store-club/byoc-stock/'),

  calculateFeeding: (data: {
    animal_type: string;
    weight_lbs?: number;
    count?: number;
    activity_level?: string;
    life_stage?: string;
    brand?: string;
  }) => apiCall('/store-club/calculate-feeding/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Cart API
export const cartAPI = {
  get: () => apiCall('/store-club/cart/'),

  addItem: (productId: number, quantity: number) =>
    apiCall('/store-club/cart/', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity }),
    }),

  updateItem: (itemId: number, quantity: number) =>
    apiCall(`/store-club/cart/items/${itemId}/`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    }),

  removeItem: (itemId: number) =>
    apiCall(`/store-club/cart/items/${itemId}/`, { method: 'DELETE' }),

  checkout: (data: {
    pickup_method: 'locker' | 'in_store';
    notes?: string;
  }) => apiCall('/store-club/orders/', {
    method: 'POST',
    body: JSON.stringify({
      pickup_method: data.pickup_method,
      notes: data.notes || '',
    }),
  }),

  // Initiate checkout with Helcim payment
  initiateCheckout: (data: {
    pickup_method: 'locker' | 'in_store';
    notes?: string;
  }) => apiCall('/store-club/checkout/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Confirm successful payment
  confirmPayment: (data: {
    order_id: number;
    transaction_id?: string;
  }) => apiCall('/store-club/checkout/confirm/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Orders API
export const ordersAPI = {
  getAll: () => apiCall('/store-club/orders/'),

  getOne: (id: number) => apiCall(`/store-club/orders/${id}/`),
};

// Locker Pickup API
export const lockerPickupAPI = {
  getActive: () => apiCall('/store-club/locker-pickup/'),
};

// Reminders API
export const remindersAPI = {
  getAll: () => apiCall('/store-club/reminders/'),

  dismiss: (id: number) => apiCall(`/store-club/reminders/${id}/dismiss/`, { method: 'POST' }),
};

// Weather Alerts API
export const weatherAlertsAPI = {
  getAll: () => apiCall('/store-club/weather-alerts/'),

  markRead: (id: number) => apiCall(`/store-club/weather-alerts/${id}/read/`, { method: 'POST' }),
};

// Animal types and options
export const ANIMAL_TYPES = [
  { value: 'dog', label: 'Dog', icon: '🐕' },
  { value: 'cat', label: 'Cat', icon: '🐈' },
  { value: 'chicken', label: 'Chickens', icon: '🐔' },
  { value: 'duck', label: 'Ducks', icon: '🦆' },
  { value: 'turkey', label: 'Turkeys', icon: '🦃' },
  { value: 'goat', label: 'Goats', icon: '🐐' },
  { value: 'sheep', label: 'Sheep', icon: '🐑' },
  { value: 'pig', label: 'Pigs', icon: '🐷' },
  { value: 'rabbit', label: 'Rabbits', icon: '🐰' },
  { value: 'horse', label: 'Horses', icon: '🐴' },
  { value: 'cow', label: 'Cattle', icon: '🐄' },
  { value: 'other', label: 'Other', icon: '🐾' },
];

export const PET_TYPES = ['dog', 'cat'];
export const FARM_TYPES = ['chicken', 'duck', 'turkey', 'goat', 'sheep', 'pig', 'rabbit', 'horse', 'cow'];

export const ACTIVITY_LEVELS = [
  { value: 'low', label: 'Low Activity', description: 'Senior, sedentary, or less active' },
  { value: 'normal', label: 'Normal Activity', description: 'Average daily activity' },
  { value: 'high', label: 'High Activity', description: 'Working dogs, very active' },
];

export const LIFE_STAGES = [
  { value: 'puppy', label: 'Puppy/Kitten', description: 'Under 1 year' },
  { value: 'adult', label: 'Adult', description: '1-7 years' },
  { value: 'senior', label: 'Senior', description: '7+ years' },
];

export const DOG_BREEDS = [
  'Mixed Breed',
  'Labrador Retriever',
  'German Shepherd',
  'Golden Retriever',
  'Australian Shepherd',
  'Border Collie',
  'Beagle',
  'Rottweiler',
  'Great Dane',
  'Chihuahua',
  'Husky',
  'Cattle Dog',
  'Other',
];

export const CAT_BREEDS = [
  'Domestic Shorthair',
  'Domestic Longhair',
  'Maine Coon',
  'Siamese',
  'Persian',
  'Ragdoll',
  'Bengal',
  'Russian Blue',
  'Other',
];

export const GOAT_BREEDS = [
  'Nigerian Dwarf',
  'Pygmy',
  'Nubian',
  'Boer',
  'Alpine',
  'LaMancha',
  'Saanen',
  'Mixed',
  'Other',
];
